import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  clearStoredAppTimezonePreference,
  normalizeAppTimezonePreference,
  writeStoredAppTimezonePreference,
  type AppTimezonePreference,
} from '@sparkai/utils'

import { apiClient } from '../apiClient'

export const USER_PREFERENCES_KEY = 'user-preferences'

type UserPreferencesResponse = AppTimezonePreference
type UserPreferencesRequest = AppTimezonePreference

export function useGetPreferences(enabled = true) {
  return useQuery({
    queryKey: [USER_PREFERENCES_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<UserPreferencesResponse | null>('/int/v3/users/me/preferences')
      if (!data) {
        clearStoredAppTimezonePreference()
        return null
      }

      const preference = normalizeAppTimezonePreference(data)
      writeStoredAppTimezonePreference(preference)
      return preference
    },
    enabled,
    refetchOnWindowFocus: false,
  })
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (preference: UserPreferencesRequest) => {
      const normalizedPreference = normalizeAppTimezonePreference(preference)
      const { data } = await apiClient.put<UserPreferencesResponse>(
        '/int/v3/users/me/preferences',
        normalizedPreference
      )
      return normalizeAppTimezonePreference(data)
    },
    onSuccess: (preference) => {
      writeStoredAppTimezonePreference(preference)
      queryClient.setQueryData([USER_PREFERENCES_KEY], preference)
    },
  })
}
